import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { useIntl } from 'react-intl';
import { getDealRoomBrandedName } from '../../../../utils/locale';
import { gql, useMutation, useQuery } from '@apollo/client';
import RjInitiateContactModal from './RjInitiateContactModal';
import Button from '../../../../../../ui/canon/Button';

// GraphQL Mutations
const INVITE_MUTATION = gql`
  mutation inviteUser($deal_interest: String!) {
    inviteUser(deal_interest: $deal_interest)
  }
`;

const SHORTLIST_DEAL_INTEREST = gql`
  mutation shortlistDealInterest($dealId: ID!, $dealInterestId: ID!) {
    shortlistDealInterest(dealId: $dealId, dealInterestId: $dealInterestId) {
      dealInterest {
        id
        workflow_state
        shortlisted
      }
      errors
    }
  }
`;

const ARCHIVE_DEAL_INTEREST = gql`
  mutation archiveDealInterest($dealId: ID!, $dealInterestId: ID!, $declined: Boolean!) {
    archiveDealInterest(dealId: $dealId, dealInterestId: $dealInterestId, declined: $declined) {
      dealInterest {
        id
        workflow_state
      }
      errors
    }
  }
`;

const SEND_NDA_TO_RECEIVING_PARTY = gql`
  mutation sendNdaToReceivingParty($dealId: ID!, $dealInterestId: ID!) {
    sendNdaToReceivingParty(dealId: $dealId, dealInterestId: $dealInterestId) {
      dealInterest {
        id
        workflow_state
        receiving_party_nda_sent_timestamp
      }
      errors
    }
  }
`;

const EXPRESS_INTEREST_MUTATION = gql`
  mutation expressInterest($id: ID!, $type: String!) {
    expressInterest(id: $id, type: $type)
  }
`;

const RESPOND_TO_EXPRESSED_INTEREST = gql`
  mutation RespondToExpressedInterest($expressed_interest_id: ID!, $decision: String!, $rationale: String) {
    respond_to_expressed_interest(expressed_interest_id: $expressed_interest_id, decision: $decision, rationale: $rationale) {
      success
      errors
    }
  }
`;

// RJ Seller Visibility Workflow — buyer-side check: has the current user
// already used their one-time initiation with this seller? Drives the
// greyed-with-tooltip state on the Message button.
const HAS_INITIATED_CONTACT_WITH = gql`
  query HasInitiatedContactWith($seller_user_id: ID!) {
    getCurrentUser {
      id
      has_initiated_contact_with(seller_user_id: $seller_user_id)
    }
  }
`;

// RJ Seller Visibility Workflow — seller-side drawer action. Fires the same
// mutation the ChatPanel Invite button uses; skips the seller having to
// navigate to Messages first.
// `conversation_id` is optional so the drawer can invite a buyer reached from
// search; `buyer_user_id` identifies them in that case. `deal_id` says WHICH
// Meeting Room — without it the server used to mint a fresh unpublished Deal on
// every click. `reason` carries the machine-readable outcome the UI branches on.
const RJ_INVITE_TO_MEETING_ROOM_FROM_DRAWER = gql`
  mutation RjInviteToMeetingRoomFromDrawer($conversation_id: ID, $buyer_user_id: ID, $deal_id: ID) {
    rj_invite_to_meeting_room(
      conversation_id: $conversation_id
      buyer_user_id: $buyer_user_id
      deal_id: $deal_id
    ) {
      success
      errors
      reason
      deal_interest_id
      deal_id
    }
  }
`;

const ProfileSidebar = ({
  user,
  isViewingSelf,
  showMessagesTab,
  showNotebookTab,
  canMessage,
  showInviteToDeal = false,
  isInRolodex,
  onMessage,
  onAddToRolodex,
  userNotes = [],
  noteText = '',
  onNoteTextChange,
  onSubmitNote,
  onDeleteNote,
  currentUser,
  company,
  dealInterest,
  obfuscateView = false,
  apolloClient,
  enqueueSnackbar,
  onScrollToDeal,
}) => {
  const intl = useIntl();
  // Separate loading states for each action
  const [isInviting, setIsInviting] = useState(false);
  const [isShortlisting, setIsShortlisting] = useState(false);
  const [isUnshortlisting, setIsUnshortlisting] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isSendingNDA, setIsSendingNDA] = useState(false);
  const [isExpressingInterest, setIsExpressingInterest] = useState(false);
  const [hasExpressedInterest, setHasExpressedInterest] = useState(user?.expressed_interest || false);
  const [expressedDate, setExpressedDate] = useState(null);
  const [expressInterestMutation] = useMutation(EXPRESS_INTEREST_MUTATION);
  const [respondToExpressedInterestMutation] = useMutation(RESPOND_TO_EXPRESSED_INTEREST);

  const companyFeatures = company?.feature_types || company?.features || [];

  // RJ Seller Visibility Workflow — divert the Message button when the buyer
  // is looking at an opted-in succession seller under `rj_seller_visibility`.
  // Mirrors the divert in SuccessionProfileDrawerNew.jsx so both drawer surfaces
  // route through RjInitiateContactModal instead of the legacy createChat path.
  //
  // The flag is part of the condition on purpose. `marketplace_visibility_opt_in`
  // is exposed on PublicUserType ungated, so a seller who opted in while the flag
  // was on still reports true after it is turned off. Without this check the
  // Message button would divert into a modal whose mutation answers "Feature not
  // enabled", and (below) the Express Interest fallback would already be hidden —
  // leaving the buyer no working action at all. Flag off must stay a no-op.
  const isRjVisibilityFlow = !!(
    companyFeatures.includes('rj_seller_visibility') &&
    currentUser?.motivation === 'growth' &&
    user?.motivation === 'succession' &&
    user?.marketplace_visibility_opt_in
  );
  const [rjModalOpen, setRjModalOpen] = useState(false);
  const [rjAlreadyInitiatedLocal, setRjAlreadyInitiatedLocal] = useState(false);
  const { data: rjInitData } = useQuery(HAS_INITIATED_CONTACT_WITH, {
    variables: { seller_user_id: user?.id?.toString() },
    skip: !isRjVisibilityFlow || !user?.id,
    fetchPolicy: 'cache-and-network',
  });
  const rjAlreadyInitiated =
    rjAlreadyInitiatedLocal || !!rjInitData?.getCurrentUser?.has_initiated_contact_with;

  // RJ Seller Visibility Workflow — seller-side drawer action. When the seller
  // (succession) is viewing a growth buyer they already have a :message
  // conversation with, offer a one-click Invite to Meeting Room here. Reduces
  // clicks vs having to navigate to /succession/messages first.
  //
  // The gate is SERVER-COMPUTED (`rj_meeting_room_invite_state`, backed by
  // `visible_profile_features_for_user`). It used to be a client-side motivation
  // check that also required an existing conversation — which meant a seller who
  // found the buyer via search could never invite from here, and which could drift
  // from what the ChatPanel showed. Do not reintroduce local derivation.
  const rjInviteState = user?.rj_meeting_room_invite_state;
  const rjBranch = rjInviteState?.branch;
  const isRjSellerViewingBuyer = rjBranch === 'can_invite' || rjBranch === 'must_create';
  const [rjInviteFromDrawerMutation, { loading: rjInvitingFromDrawer }] =
    useMutation(RJ_INVITE_TO_MEETING_ROOM_FROM_DRAWER);
  const [rjInviteFromDrawerSent, setRjInviteFromDrawerSent] = useState(false);

  // Branch (a): no Meeting Room yet — send the seller to the create flow, carrying
  // the buyer so the invite completes on the far side.
  const goToMeetingRoomWizard = () => {
    if (typeof window !== 'undefined' && window.ahoy) {
      window.ahoy.track('rj_meeting_room_create_started_from_drawer', { buyer_user_id: user?.id });
    }
    // Succession wizard, NOT the legacy /deal/create — that one publishes via
    // `updateDeal` and would never complete the invite.
    window.location.href =
      `/succession/deals/wizard${user?.id ? `?invite_buyer_id=${user.id}` : ''}`;
  };

  const handleRjInviteFromDrawer = async () => {
    if (rjBranch === 'must_create') {
      goToMeetingRoomWizard();
      return;
    }

    // `conversation_id` is optional server-side now, so the drawer can invite a
    // buyer reached from search. Pass it when we have one (it carries its own
    // participant authorization); otherwise identify the buyer directly.
    const convoId = user?.conversation_with_me?.id;
    const variables = { deal_id: rjInviteState?.deal_id || null };
    if (convoId) {
      variables.conversation_id = convoId.toString();
    } else {
      variables.buyer_user_id = user?.id?.toString();
    }
    if (!variables.conversation_id && !variables.buyer_user_id) return;

    try {
      const result = await rjInviteFromDrawerMutation({ variables });
      const payload = result?.data?.rj_invite_to_meeting_room;

      if (payload?.reason === 'no_meeting_room') {
        goToMeetingRoomWizard();
        return;
      }
      if (payload?.success) {
        setRjInviteFromDrawerSent(true);
        if (typeof window !== 'undefined' && window.ahoy) {
          window.ahoy.track('rj_meeting_room_invited_from_drawer', {
            conversation_id: convoId || null,
            buyer_user_id: user?.id,
            deal_id: payload.deal_id,
            deal_interest_id: payload.deal_interest_id,
          });
        }
        enqueueSnackbar?.(`Meeting Room invite sent to ${user?.name || user?.allowed_name || 'this buyer'}.`, {
          variant: 'success',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
        });
      } else {
        enqueueSnackbar?.((payload?.errors || ['Failed to send invite']).join(', '), { variant: 'error' });
      }
    } catch (err) {
      console.error('[RJ InviteFromDrawer] failed:', err);
      enqueueSnackbar?.('Failed to send invite. Please try again.', { variant: 'error' });
    }
  };
  const [incomingEoiLocalState, setIncomingEoiLocalState] = useState(user?.incoming_eoi_consent_state || null);
  const [showDeclineForm, setShowDeclineForm] = useState(false);
  const [rationale, setRationale] = useState('');
  const [isRespondingToEoi, setIsRespondingToEoi] = useState(false);

  // companyFeatures is declared above, alongside isRjVisibilityFlow.
  const hasMarketplace = companyFeatures.includes('marketplace_sellers_visible');
  const hasEoiConsent = companyFeatures.includes('per_eoi_seller_consent');
  const eoiConsentState = hasEoiConsent ? (user?.eoi_consent_state || null) : null;
  const incomingEoiId = hasEoiConsent ? (user?.incoming_eoi_id || null) : null;
  const isGrowthUser = currentUser?.motivation === 'growth';
  const isSuccessionProfile = user?.motivation === 'succession';

  // Express Interest is suppressed wherever the RJ Seller Visibility flow owns the
  // interaction — i.e. exactly where the Message button diverts to
  // RjInitiateContactModal. The two are deliberately driven by the same condition
  // so they cannot drift: one intent, one call to action.
  //
  // WHY: under this flow the seller has already consented by opting in, so asking
  // the buyer to separately "let the seller know" requests permission that was
  // already granted. It also weakens the one-introduction-per-seller rule by giving
  // the buyer a second way to reach the same person. Admins are still notified when
  // the seller invites the buyer to a Meeting Room, so no oversight signal is lost.
  //
  // Scoped to this pairing only. Non-opted-in sellers, partial-sale surfaces and
  // every other tenant keep Express Interest exactly as before.
  const showExpressInterest =
    hasMarketplace && isGrowthUser && isSuccessionProfile && !isViewingSelf && !isRjVisibilityFlow;

  const handleExpressInterest = async () => {
    setIsExpressingInterest(true);
    try {
      await expressInterestMutation({ variables: { id: parseInt(user.id, 10), type: 'managed' } });
      setHasExpressedInterest(true);
      setExpressedDate(new Date());
      enqueueSnackbar?.(intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.express_interest_success', defaultMessage: 'Interest expressed successfully!' }), { variant: 'success' });
    } catch (err) {
      console.error('Express interest failed:', err);
      enqueueSnackbar?.(intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.express_interest_error', defaultMessage: 'Failed to express interest. Please try again.' }), { variant: 'error' });
    } finally {
      setIsExpressingInterest(false);
    }
  };
  
  const handleEoiRespond = async (decision) => {
    if (!incomingEoiId) return;
    setIsRespondingToEoi(true);
    try {
      const result = await respondToExpressedInterestMutation({
        variables: {
          expressed_interest_id: incomingEoiId,
          decision,
          rationale: decision === 'decline' ? rationale : undefined,
        }
      });
      const payload = result?.data?.respond_to_expressed_interest;
      if (payload?.success) {
        setIncomingEoiLocalState(decision === 'accept' ? 'accepted' : 'declined');
        setShowDeclineForm(false);
        setRationale('');
        const buyerName = user?.allowed_name || user?.fb_user_name || 'This buyer';
        enqueueSnackbar?.(
          decision === 'accept'
            ? `${buyerName} has been accepted — you are now free to message each other.`
            : intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.eoi_respond_declined', defaultMessage: 'Interest declined.' }),
          { variant: decision === 'accept' ? 'success' : 'info' }
        );
      } else {
        enqueueSnackbar?.(payload?.errors?.join(', ') || intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.eoi_respond_error', defaultMessage: 'Failed to respond. Please try again.' }), { variant: 'error' });
      }
    } catch (err) {
      console.error('EOI respond failed:', err);
      enqueueSnackbar?.(intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.eoi_respond_error', defaultMessage: 'Failed to respond. Please try again.' }), { variant: 'error' });
    } finally {
      setIsRespondingToEoi(false);
    }
  };

  // Combined loading state for disabling all buttons
  const isLoading = isInviting || isShortlisting || isUnshortlisting || isArchiving || isSendingNDA;
  const [localWorkflowState, setLocalWorkflowState] = useState(get(dealInterest, 'workflow_state'));
  const workflowState = localWorkflowState;
  const dealId = get(dealInterest, 'deal.id');
  const dealInterestId = get(dealInterest, 'id');
  const dealName = get(dealInterest, 'deal.deal_name');

  useEffect(() => {
    setLocalWorkflowState(get(dealInterest, 'workflow_state'));
  }, [dealInterest?.id, dealInterest?.workflow_state]);

  useEffect(() => {
    setIncomingEoiLocalState(user?.incoming_eoi_consent_state || null);
    setShowDeclineForm(false);
    setRationale('');
  }, [user?.id, user?.incoming_eoi_consent_state]);
  
  // Format date helper
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const locale = (typeof window !== 'undefined' && window.locale) || 'en';
    return date.toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handler functions for deal interest actions
  const handleInvite = async () => {
    if (!apolloClient || !dealInterestId) return;
    try {
      setIsInviting(true);
      await apolloClient.mutate({
        mutation: INVITE_MUTATION,
        variables: { deal_interest: dealInterestId }
      });
      if (enqueueSnackbar) enqueueSnackbar(intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.toast_invitation_sent' }), { variant: 'success' });
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      console.error('Error inviting user:', error);
      if (enqueueSnackbar) enqueueSnackbar(intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.toast_invitation_failed' }), { variant: 'error' });
    } finally {
      setIsInviting(false);
    }
  };

  const handleShortlist = async () => {
    if (!apolloClient || !dealId || !dealInterestId) return;
    try {
      setIsShortlisting(true);
      const response = await apolloClient.mutate({
        mutation: SHORTLIST_DEAL_INTEREST,
        variables: { dealId, dealInterestId }
      });
      const updatedWorkflowState = response?.data?.shortlistDealInterest?.dealInterest?.workflow_state;
      if (updatedWorkflowState) {
        setLocalWorkflowState(updatedWorkflowState);
      }
      if (enqueueSnackbar) enqueueSnackbar(intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.toast_shortlisted' }), { variant: 'success' });
    } catch (error) {
      console.error('Error shortlisting advisor:', error);
      if (enqueueSnackbar) enqueueSnackbar(intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.toast_shortlist_failed' }), { variant: 'error' });
    } finally {
      setIsShortlisting(false);
    }
  };

  const handleUnshortlist = async () => {
    if (!apolloClient || !dealId || !dealInterestId) return;
    try {
      setIsUnshortlisting(true);
      // Use the same SHORTLIST_DEAL_INTEREST mutation - it toggles based on current state
      const response = await apolloClient.mutate({
        mutation: SHORTLIST_DEAL_INTEREST,
        variables: { dealId, dealInterestId }
      });
      const updatedWorkflowState = response?.data?.shortlistDealInterest?.dealInterest?.workflow_state;
      if (updatedWorkflowState) {
        setLocalWorkflowState(updatedWorkflowState);
      }
      if (enqueueSnackbar) enqueueSnackbar(intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.toast_removed_from_shortlist' }), { variant: 'success' });
    } catch (error) {
      console.error('Error removing from shortlist:', error);
      if (enqueueSnackbar) enqueueSnackbar(intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.toast_remove_shortlist_failed' }), { variant: 'error' });
    } finally {
      setIsUnshortlisting(false);
    }
  };

  const handleArchive = async (declined = false) => {
    if (!apolloClient || !dealId || !dealInterestId) return;
    try {
      setIsArchiving(true);
      const response = await apolloClient.mutate({
        mutation: ARCHIVE_DEAL_INTEREST,
        variables: { dealId, dealInterestId, declined }
      });
      const updatedWorkflowState = response?.data?.archiveDealInterest?.dealInterest?.workflow_state;
      if (updatedWorkflowState) {
        setLocalWorkflowState(updatedWorkflowState);
      }
      if (enqueueSnackbar) enqueueSnackbar(intl.formatMessage({ id: declined ? 'succession.profile_drawer.profile_sidebar.toast_advisor_declined' : 'succession.profile_drawer.profile_sidebar.toast_advisor_archived' }), { variant: 'success' });
    } catch (error) {
      console.error('Error archiving advisor:', error);
      if (enqueueSnackbar) enqueueSnackbar(intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.toast_archive_failed' }), { variant: 'error' });
    } finally {
      setIsArchiving(false);
    }
  };

  const handleSendNDA = async () => {
    if (!apolloClient || !dealId || !dealInterestId) return;
    try {
      setIsSendingNDA(true);
      const response = await apolloClient.mutate({
        mutation: SEND_NDA_TO_RECEIVING_PARTY,
        variables: { dealId, dealInterestId }
      });
      const updatedWorkflowState = response?.data?.sendNdaToReceivingParty?.dealInterest?.workflow_state;
      if (updatedWorkflowState) {
        setLocalWorkflowState(updatedWorkflowState);
      }
      
      if (response.data?.sendNdaToReceivingParty?.dealInterest) {
        if (enqueueSnackbar) enqueueSnackbar(intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.toast_nda_sent' }), { variant: 'success' });
        setTimeout(() => {
          window.location.href = `/succession/deals/${dealName}/prospects/ndas`;
        }, 1500);
      } else if (response.data?.sendNdaToReceivingParty?.errors?.length) {
        if (enqueueSnackbar) enqueueSnackbar(response.data.sendNdaToReceivingParty.errors.join(', '), { variant: 'error' });
      }
    } catch (error) {
      console.error('Error sending NDA:', error);
      if (enqueueSnackbar) enqueueSnackbar(intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.toast_nda_failed' }), { variant: 'error' });
    } finally {
      setIsSendingNDA(false);
    }
  };

  const handleSignNDA = () => {
    const advisorId = get(dealInterest, 'inventory_user.user.id');
    const matchId = dealInterestId;
    const returnUrl = `${encodeURIComponent(window.location.href.split('?')[0])}/return/${matchId}`;
    window.location.href = `/deal/nda_sign_discloser?deal_id=${dealId}&match_id=${matchId}&advisor_id=${advisorId}&return_url=${returnUrl}`;
  };

  const handleDownloadNDA = () => {
    window.open(`/deal/${dealName}/open_file/${dealInterestId}`, '_blank');
  };

  const InfoSection = ({ title, children }) => (
    <div className="space-y-2 pb-4 border-b border-succession-border last:border-b-0 last:pb-0">
      <h4 className="font-semibold text-succession-text-primary text-sm">{title}</h4>
      <div className="text-sm text-succession-text-muted">{children}</div>
    </div>
  );

  // Helper function to format display text: remove underscores and capitalize each word
  const formatDisplayText = (text) => {
    if (!text) return '';
    
    return text
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const renderList = (items, emptyText = intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.not_specified' })) => {
    if (!items || items.length === 0) {
      return <span className="italic">{emptyText}</span>;
    }
    return (
      <div className="space-y-1">
        {items.map((item, index) => {
          // Handle both string and object formats from GraphQL
          const itemText = typeof item === 'string' ? item : item?.name || item?.id || '';
          return (
            <div key={item?.id || index}>{formatDisplayText(itemText)}</div>
          );
        })}
      </div>
    );
  };

  const renderBadges = (items, emptyText = intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.none' })) => {
    if (!items || items.length === 0) {
      return <span className="italic">{emptyText}</span>;
    }
    return (
      <div className="flex flex-wrap gap-2">
        {items.map((item, index) => {
          // Handle both string and object formats from GraphQL
          const itemText = typeof item === 'string' ? item : item?.name || item?.id || '';
          return (
            <span
              key={item?.id || index}
              className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-succession-bg-primary border border-succession-border text-succession-text-primary"
            >
              {formatDisplayText(itemText)}
            </span>
          );
        })}
      </div>
    );
  };

  // Get status indicator color based on deal status or workflow state
  const getStatusColor = () => {
    // If viewing a deal interest, use workflow state colors
    if (workflowState) {
      const workflowColorMap = {
        'matching': 'bg-green-500',
        'invited': 'bg-blue-500',
        'reviewing_proposal': 'bg-yellow-500',
        'shortlisted': 'bg-purple-500',
        'out_for_receiving_signature': 'bg-orange-600',
        'awaiting_disclosing_signature': 'bg-red-500',
        'nda_completed': 'bg-teal-500',
        'negotiating': 'bg-indigo-500',
        'declined': 'bg-gray-500',
        'archived': 'bg-amber-700'
      };
      return workflowColorMap[workflowState] || 'bg-green-500';
    }
    
    // Otherwise use user status color
    if (!user.statusColor) return 'bg-green-500';
    
    const colorMap = {
      'green': 'bg-green-500',
      'blue': 'bg-blue-500',
      'orange': 'bg-orange-500',
      'purple': 'bg-purple-500',
      'deeporange': 'bg-orange-600',
      'red': 'bg-red-500',
      'teal': 'bg-teal-500',
      'indigo': 'bg-indigo-500',
      'grey': 'bg-gray-500',
      'brown': 'bg-amber-700'
    };
    
    return colorMap[user.statusColor] || 'bg-green-500';
  };

  // Get workflow state display name
  const getWorkflowStateDisplay = () => {
    if (!workflowState) return null;
    const stateMap = {
      'matching': intl.formatMessage({ id: 'succession.profile_drawer.deal_section.state_matching' }),
      'invited': intl.formatMessage({ id: 'succession.profile_drawer.deal_section.state_invited' }),
      'reviewing_proposal': intl.formatMessage({ id: 'succession.profile_drawer.deal_section.state_reviewing_proposal' }),
      'shortlisted': intl.formatMessage({ id: 'succession.profile_drawer.deal_section.state_shortlisted' }),
      'out_for_receiving_signature': intl.formatMessage({ id: 'succession.profile_drawer.deal_section.state_nda_out_for_signature' }),
      'awaiting_disclosing_signature': intl.formatMessage({ id: 'succession.profile_drawer.deal_section.state_awaiting_your_signature' }),
      'nda_completed': intl.formatMessage({ id: 'succession.profile_drawer.deal_section.state_nda_completed' }),
      'negotiating': intl.formatMessage({ id: 'succession.profile_drawer.deal_section.state_negotiating' }),
      'declined': intl.formatMessage({ id: 'succession.profile_drawer.deal_section.state_declined' }),
      'declined_by_buyer': intl.formatMessage({ id: 'succession.profile_drawer.deal_section.state_declined_by_buyer' }),
      'archived': intl.formatMessage({ id: 'succession.profile_drawer.deal_section.state_archived' }),
    };
    return stateMap[workflowState] || workflowState;
  };

  // Render workflow-specific action buttons
  const renderWorkflowActions = () => {
    if (!workflowState) return null;

    switch (workflowState) {
      case 'matching':
        return (
          <button
            onClick={handleInvite}
            disabled={isLoading}
            className="w-full px-4 py-2 bg-succession-primary text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium mt-3"
          >
            {isInviting ? intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.sending' }) : intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.invite_to_deal' })}
          </button>
        );

      case 'invited':
        return (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center mt-3">
            <p className="text-blue-700 font-medium text-sm">{intl.formatMessage({ id: 'succession.profile_drawer.deal_section.invitation_sent' })}</p>
            <p className="text-xs text-blue-600 mt-1">{intl.formatMessage({ id: 'succession.profile_drawer.deal_section.waiting_for_response' })}</p>
          </div>
        );

      case 'reviewing_proposal':
        return (
          <div className="space-y-2 mt-3">
            {onScrollToDeal && (
              <button
                onClick={onScrollToDeal}
                className="w-full px-4 py-2 bg-succession-primary text-white rounded-lg hover:opacity-90 transition-opacity text-sm font-medium flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                {intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.view_deal_information' })}
              </button>
            )}
            <button
              onClick={handleShortlist}
              disabled={isLoading}
              className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
            >
              {isShortlisting ? intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.processing' }) : intl.formatMessage({ id: 'succession.profile_drawer.deal_section.shortlist_advisor' })}
            </button>
            <button
              onClick={() => handleArchive(false)}
              disabled={isLoading}
              className="w-full px-4 py-2 border border-succession-border text-succession-text-primary rounded-lg hover:bg-succession-bg-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              {intl.formatMessage({ id: 'succession.profile_drawer.deal_section.archive' })}
            </button>
          </div>
        );

      case 'shortlisted':
        return (
          <div className="space-y-2 mt-3">
            <button
              onClick={handleSendNDA}
              disabled={isLoading}
              className="w-full px-4 py-2 bg-succession-primary text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
            >
              {isSendingNDA ? intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.sending' }) : intl.formatMessage({ id: 'succession.profile_drawer.deal_section.send_nda' })}
            </button>
            <button
              onClick={handleUnshortlist}
              disabled={isLoading}
              className="w-full px-4 py-2 border border-succession-border text-succession-text-primary rounded-lg hover:bg-succession-bg-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              {isUnshortlisting ? intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.processing' }) : intl.formatMessage({ id: 'succession.profile_drawer.deal_section.remove_from_shortlist' })}
            </button>
            <button
              onClick={() => handleArchive(true)}
              disabled={isLoading}
              className="w-full px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              {isArchiving ? intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.processing' }) : intl.formatMessage({ id: 'succession.profile_drawer.deal_section.decline' })}
            </button>
          </div>
        );

      case 'out_for_receiving_signature':
        return (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-center mt-3">
            <p className="text-orange-700 font-medium text-sm">{intl.formatMessage({ id: 'succession.profile_drawer.deal_section.state_nda_out_for_signature' })}</p>
            <p className="text-xs text-orange-600 mt-1">{intl.formatMessage({ id: 'succession.profile_drawer.deal_section.nda_waiting_for_sign' })}</p>
            {dealInterest?.receiving_party_nda_sent_timestamp && (
              <p className="text-xs text-orange-600 mt-2">
                {intl.formatMessage({ id: 'succession.profile_drawer.deal_section.sent_label' })} {formatDate(dealInterest.receiving_party_nda_sent_timestamp)}
              </p>
            )}
          </div>
        );

      case 'awaiting_disclosing_signature':
        return (
          <button
            onClick={handleSignNDA}
            className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:opacity-90 transition-opacity text-sm font-medium mt-3"
          >
            {intl.formatMessage({ id: 'succession.profile_drawer.deal_section.sign_nda' })}
          </button>
        );

      case 'nda_completed':
      case 'negotiating':
        return (
          <div className="space-y-2 mt-3">
            <button
              onClick={handleDownloadNDA}
              className="w-full px-4 py-2 bg-succession-primary text-white rounded-lg hover:opacity-90 transition-opacity text-sm font-medium"
            >
              {intl.formatMessage({ id: 'succession.profile_drawer.deal_section.download_nda' })}
            </button>
            {workflowState === 'negotiating' && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-center">
                <p className="text-indigo-700 font-medium text-sm">{intl.formatMessage({ id: 'succession.profile_drawer.deal_section.negotiation_phase' })}</p>
                <p className="text-xs text-indigo-600 mt-1">{intl.formatMessage({ id: 'succession.profile_drawer.deal_section.use_messaging' })}</p>
              </div>
            )}
          </div>
        );

      case 'archived':
      case 'declined':
        return (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center mt-3">
            <p className="text-gray-700 font-medium text-sm">{workflowState === 'declined' ? intl.formatMessage({ id: 'succession.profile_drawer.deal_section.state_declined' }) : intl.formatMessage({ id: 'succession.profile_drawer.deal_section.state_archived' })}</p>
            <p className="text-xs text-gray-600 mt-1">{intl.formatMessage({ id: 'succession.profile_drawer.deal_section.opportunity_inactive' })}</p>
          </div>
        );

      default:
        return null;
    }
  };

  // Get status message based on permissions and context - matching legacy StatusSection logic
  const getStatusMessage = () => {
    const companyHasNewMessages = company?.new_messages || false;
    const companyHasNetworking = company?.networking || false;
    const unsubscribed = !currentUser?.is_fb_subscriber;
    const isStudent = user?.is_student && user?.company?.enabled_student_profiles;
    const hasOpenDeal = currentUser?.has_deal && dealInterest;
    const showCreateDealButton = company?.business_type === 'wealth_rj' && 
                                  currentUser?.motivation === 'succession' &&
                                  currentUser?.approval_state === 'approved' &&
                                  currentUser?.can_match;
    const dealRoomName = getDealRoomBrandedName(company) || intl.formatMessage({ id: 'succession.messaging.chat_panel.deal_room_label' });
    const currentCompanySellerWord = company?.seller_word || 'Partner';
    const expressedInterest = user?.expressed_interest;
    const isLimitedAccess = currentUser?.limited_access;
    
    // Debug logging
    console.log('🔍 Status Message Debug:', {
      canMessage,
      showMessagesTab,
      companyHasNewMessages,
      companyHasNetworking,
      isInRolodex,
      unsubscribed,
      isStudent,
      hasOpenDeal,
      showCreateDealButton,
      isLimitedAccess,
      obfuscateView,
      expressedInterest
    });
    
    // Priority 1: Student profile
    if (isStudent) {
      const studentWord = user?.company?.student_word || 'student';
      return intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.status_student' }, { studentWord });
    }

    // Priority 2: User has open deal and should add to rolodex (only if NOT already in rolodex)
    if (hasOpenDeal && showCreateDealButton && !isInRolodex) {
      return intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.status_open_deal' });
    }

    // Priority 3: Unsubscribed user
    if (unsubscribed && !canMessage) {
      return intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.status_unsubscribed' });
    }

    // Priority 4: Show create deal button (RJ succession users without deal)
    if (showCreateDealButton && !currentUser?.has_deal) {
      return intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.status_create_deal' }, { dealRoomName });
    }

    // Priority 5: Limited access users - expressed interest
    if (isLimitedAccess && expressedInterest) {
      const userName = user?.name || user?.user_name;
      return intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.status_limited_expressed' }, { sellerWord: currentCompanySellerWord, userName });
    }

    // Priority 6: Limited access users - not expressed
    if (isLimitedAccess && !expressedInterest) {
      const userName = user?.name || user?.user_name;
      return intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.status_limited_not_expressed' }, { userName, sellerWord: currentCompanySellerWord });
    }

    // Priority 7: Obfuscated - expressed interest
    if (obfuscateView && expressedInterest) {
      const userName = user?.name || user?.user_name;
      return intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.status_obfuscated_expressed' }, { userName });
    }

    // Priority 8: Obfuscated - not expressed (needs express interest button)
    if (obfuscateView && !expressedInterest) {
      return intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.status_obfuscated_not_expressed' });
    }

    // Priority 9: Regular - free to message (relaxed conditions)
    if (canMessage && showMessagesTab) {
      return intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.status_free_to_message' });
    }

    // Priority 10: Networking disabled - but don't show if they can already message
    if (!companyHasNetworking && !showMessagesTab) {
      return intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.status_review_profile' });
    }

    // Default: Show rolodex message (only if not already in rolodex)
    if (!isInRolodex) {
      return intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.status_review_profile' });
    }

    // If already in rolodex and none of the above conditions match
    return intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.status_in_rolodex' });
  };

  return (
    <div className="space-y-6">
      {/* Status Section - Show contextual message */}
      {!isViewingSelf && (
        <div className="bg-succession-bg-secondary rounded-lg border border-succession-border p-4">
          <h4 className="text-sm font-semibold text-succession-text-primary mb-3">{intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.status_heading' })}</h4>
          
          {/* Status Badge */}
          <div className="flex items-center gap-2 mb-3">
            <div className={`w-2 h-2 rounded-full ${getStatusColor()}`}></div>
            <p className="text-sm text-succession-text-primary font-medium">
              {getWorkflowStateDisplay() || user.status || intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.available' })}
            </p>
          </div>
          
          {/* Status Message - only show if not in a deal context */}
          {!workflowState && (
            <p className="text-xs text-succession-text-muted leading-relaxed">
              {getStatusMessage()}
            </p>
          )}
          
          {/* Workflow-specific action buttons */}
          {renderWorkflowActions()}
        </div>
      )}

      {/* Express Interest — marketplace flow */}
      {showExpressInterest && (
        <div className="bg-succession-bg-secondary rounded-lg border border-succession-border p-4">
          {/* Per-EOI consent state badges (when per_eoi_seller_consent is on) */}
          {hasEoiConsent && eoiConsentState === 'accepted' && (
            <div className="text-center">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-2">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-sm font-semibold text-green-700">
                {intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.eoi_accepted', defaultMessage: 'Accepted' })}
              </p>
              <p className="text-xs text-succession-text-muted mt-1">
                {intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.eoi_accepted_desc', defaultMessage: 'The seller accepted your interest.' })}
              </p>
            </div>
          )}

          {hasEoiConsent && eoiConsentState === 'declined' && (
            <div className="text-center">
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-2">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-sm font-semibold text-succession-text-primary">
                {intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.eoi_declined', defaultMessage: 'Not Proceeding' })}
              </p>
              <p className="text-xs text-succession-text-muted mt-1">
                {intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.eoi_declined_desc', defaultMessage: 'The seller has decided not to proceed at this time.' })}
              </p>
            </div>
          )}

          {hasEoiConsent && eoiConsentState === 'pending_seller_review' && (
            <div className="text-center">
              <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-2">
                <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-sm font-semibold text-yellow-700">
                {intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.eoi_pending', defaultMessage: 'Pending Review' })}
              </p>
              <p className="text-xs text-succession-text-muted mt-1">
                {intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.eoi_pending_desc', defaultMessage: 'Awaiting seller response.' })}
              </p>
            </div>
          )}

          {/* Optimistic pending — buyer just submitted but query hasn't refetched yet */}
          {hasEoiConsent && hasExpressedInterest && !eoiConsentState && (
            <div className="text-center">
              <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-2">
                <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-sm font-semibold text-yellow-700">
                {intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.eoi_pending', defaultMessage: 'Pending Review' })}
              </p>
              <p className="text-xs text-succession-text-muted mt-1">
                {intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.eoi_pending_desc', defaultMessage: 'Awaiting seller response.' })}
              </p>
            </div>
          )}

          {/* Legacy "Interest Expressed" for non-EOI flow or unknown EOI state */}
          {(!hasEoiConsent || (hasEoiConsent && eoiConsentState && !['accepted', 'declined', 'pending_seller_review'].includes(eoiConsentState))) && hasExpressedInterest && (
            <div className="text-center">
              <div className="w-10 h-10 rounded-full bg-succession-success-10 flex items-center justify-center mx-auto mb-2">
                <svg className="w-5 h-5 text-succession-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-sm font-medium text-succession-text-primary">
                {intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.interest_expressed', defaultMessage: 'Interest Expressed' })}
              </p>
              {expressedDate && (
                <p className="text-xs text-succession-text-muted mt-1">
                  on {expressedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
              )}
              {!expressedDate && (
                <p className="text-xs text-succession-text-muted mt-1">
                  {intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.interest_expressed_desc', defaultMessage: 'The seller and admin have been notified.' })}
                </p>
              )}
            </div>
          )}

          {/* Express Interest button — shown when no EOI exists yet */}
          {(!hasEoiConsent && !hasExpressedInterest) || (hasEoiConsent && !eoiConsentState && !hasExpressedInterest) ? (
            <>
              <p className="text-xs text-succession-text-muted mb-3">
                {intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.express_interest_desc', defaultMessage: 'Interested in this opportunity? Let the seller know.' })}
              </p>
              <button
                onClick={handleExpressInterest}
                disabled={isExpressingInterest}
                className="w-full px-4 py-2 bg-succession-primary text-white rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isExpressingInterest ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.expressing', defaultMessage: 'Submitting...' })}
                  </>
                ) : (
                  intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.express_interest_button', defaultMessage: 'Express Interest' })
                )}
              </button>
            </>
          ) : null}
        </div>
      )}

      {/* Seller incoming EOI — shown when per_eoi_seller_consent is on and this buyer has sent an EOI to the current user */}
      {hasEoiConsent && !isViewingSelf && incomingEoiId && (
        <div className="bg-succession-bg-secondary rounded-lg border border-succession-border p-4">
          <h4 className="text-sm font-semibold text-succession-text-primary mb-3">
            {intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.eoi_incoming_heading', defaultMessage: 'Expression of Interest' })}
          </h4>

          {/* Pending — Accept / Decline buttons */}
          {incomingEoiLocalState === 'pending_seller_review' && (
            <>
              <p className="text-xs text-succession-text-muted mb-3">
                {intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.eoi_incoming_pending_desc', defaultMessage: 'This buyer has expressed interest in connecting with you.' })}
              </p>
              {!showDeclineForm ? (
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEoiRespond('accept')}
                    disabled={isRespondingToEoi}
                    className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.eoi_incoming_accept', defaultMessage: 'Accept' })}
                  </button>
                  <button
                    onClick={() => setShowDeclineForm(true)}
                    disabled={isRespondingToEoi}
                    className="flex-1 px-3 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 text-sm font-medium disabled:opacity-50"
                  >
                    {intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.eoi_incoming_decline', defaultMessage: 'Decline' })}
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={rationale}
                    onChange={(e) => setRationale(e.target.value)}
                    placeholder={intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.eoi_incoming_rationale_placeholder', defaultMessage: 'Reason (optional)' })}
                    className="w-full text-xs border border-succession-border rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-succession-primary bg-white text-succession-text-primary"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEoiRespond('decline')}
                      disabled={isRespondingToEoi}
                      className="flex-1 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium disabled:opacity-50"
                    >
                      {isRespondingToEoi
                        ? intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.processing', defaultMessage: 'Processing...' })
                        : intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.eoi_incoming_decline_confirm', defaultMessage: 'Confirm Decline' })}
                    </button>
                    <button
                      onClick={() => { setShowDeclineForm(false); setRationale(''); }}
                      disabled={isRespondingToEoi}
                      className="px-3 py-2 text-succession-text-muted hover:text-succession-text-primary text-sm"
                    >
                      {intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.eoi_incoming_decline_cancel', defaultMessage: 'Cancel' })}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Accepted */}
          {incomingEoiLocalState === 'accepted' && (
            <div className="text-center">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-2">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-sm font-semibold text-green-700">
                {intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.eoi_incoming_accepted', defaultMessage: 'Accepted' })}
              </p>
              <p className="text-xs text-succession-text-muted mt-1">
                {intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.eoi_incoming_accepted_desc', defaultMessage: "You accepted this buyer's expression of interest." })}
              </p>
            </div>
          )}

          {/* Declined */}
          {incomingEoiLocalState === 'declined' && (
            <div className="text-center">
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-2">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-sm font-semibold text-succession-text-primary">
                {intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.eoi_incoming_declined', defaultMessage: 'Declined' })}
              </p>
              <p className="text-xs text-succession-text-muted mt-1">
                {intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.eoi_incoming_declined_desc', defaultMessage: "You declined this buyer's expression of interest." })}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Buyer Readiness — admin/staff/customer only */}
      {(() => {
        const bar = user?.buyer_assessment_result;
        const currentRoles = currentUser?.roles || [];
        const isPrivileged = currentRoles.includes('admin') || currentRoles.includes('customer') || currentRoles.includes('staff');
        if (!bar || !isPrivileged) return null;

        const bandColorMap = {
          high: 'bg-succession-success-10 text-succession-success',
          moderate: 'bg-succession-warning-10 text-succession-warning',
          low: 'bg-succession-accent-10 text-succession-accent',
          not_ready: 'bg-succession-error-bg text-succession-error',
        };
        const badgeClasses = bandColorMap[bar.readinessBand || bar.readiness_band] || bandColorMap.not_ready;
        const completedDate = (bar.completedAt || bar.completed_at)
          ? new Date(bar.completedAt || bar.completed_at).toLocaleDateString(intl.locale || 'en', { month: 'short', day: 'numeric', year: 'numeric' })
          : null;

        return (
          <div className="bg-succession-bg-secondary rounded-lg border border-succession-border p-4">
            <h4 className="text-sm font-semibold text-succession-text-primary mb-3">
              {intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.buyer_readiness', defaultMessage: 'Buyer Readiness' })}
            </h4>
            <div className="flex items-center gap-2 mb-2">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold ${badgeClasses}`}>
                {bar.bandLabel || bar.band_label}
              </span>
            </div>
            <div className="text-xs text-succession-text-muted space-y-1">
              <div className="flex justify-between">
                <span>{intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.buyer_readiness_score', defaultMessage: 'Score' })}</span>
                <span className="font-medium text-succession-text-primary">{bar.totalScore || bar.total_score}</span>
              </div>
              {completedDate && (
                <div className="flex justify-between">
                  <span>{intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.buyer_readiness_completed', defaultMessage: 'Completed' })}</span>
                  <span className="font-medium text-succession-text-primary">{completedDate}</span>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Action Buttons */}
      {!isViewingSelf && (showMessagesTab || showNotebookTab || showInviteToDeal || isRjSellerViewingBuyer) && (
        <div className="bg-succession-bg-secondary rounded-lg border border-succession-border p-4">
          <div className="space-y-2">
            {showMessagesTab && canMessage && (
              isRjVisibilityFlow ? (
                // RJ Seller Visibility Workflow — divert to the one-time initiation
                // modal. Button renders greyed with tooltip after the buyer has used
                // their one shot (server-computed via has_initiated_contact_with).
                <Button
                  variant="primary"
                  size="md"
                  onClick={() => setRjModalOpen(true)}
                  disabled={rjAlreadyInitiated}
                  title={rjAlreadyInitiated ? "You've already reached out to this advisor." : undefined}
                  className="w-full"
                >
                  {rjAlreadyInitiated
                    ? "You've already reached out"
                    : intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.message_seller', defaultMessage: 'Message Seller' })}
                </Button>
              ) : (
                <Button variant="primary" size="md" onClick={onMessage} className="w-full">
                  {intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.message' })}
                </Button>
              )
            )}

            {/* RJ Seller Visibility Workflow — seller-side quick action.
                Only shows when the seller is viewing a growth buyer they already
                have an active :message conversation with. Skip the trip to
                /succession/messages when the seller decides mid-review to invite
                to a Meeting Room. Fires the same rj_invite_to_meeting_room
                mutation the ChatPanel button uses. */}
            {isRjSellerViewingBuyer && !rjInviteFromDrawerSent && (
              <Button
                variant="primary"
                size="md"
                onClick={handleRjInviteFromDrawer}
                disabled={rjInvitingFromDrawer}
                className="w-full"
                data-rj-branch={rjBranch}
              >
                {rjInvitingFromDrawer
                  ? 'Inviting…'
                  : rjBranch === 'must_create'
                    ? 'Create Meeting Room'
                    : 'Invite to Meeting Room'}
              </Button>
            )}
            {isRjSellerViewingBuyer && rjInviteFromDrawerSent && (
              <div className="w-full px-4 py-2 rounded-md bg-succession-success-10 text-succession-success text-sm text-center">
                Meeting Room invite sent.
              </div>
            )}
            {showInviteToDeal && !workflowState && (
              <button
                onClick={onAddToRolodex}
                className="w-full px-4 py-2 bg-succession-primary text-white rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
                {intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.invite_to_deal' })}
              </button>
            )}
            {showNotebookTab && (
              <button
                onClick={onAddToRolodex}
                className="w-full px-4 py-2 border border-succession-border rounded-lg text-succession-text-primary hover:bg-succession-bg-primary transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  {isInRolodex ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  )}
                </svg>
                {isInRolodex ? intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.in_rolodex' }) : intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.add_to_rolodex' })}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Main Information */}
      <div className="bg-succession-bg-secondary rounded-lg border border-succession-border p-4">
        <div className="space-y-4">
          {user.continuity_plan && (
            <InfoSection title={intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.continuity_plan' })}>
              {user.continuity_plan}
            </InfoSection>
          )}

          {user.languages && user.languages.length > 0 && (
            <InfoSection title={intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.languages' })}>
              {user.languages.map(lang => {
                const langText = typeof lang === 'string' ? lang : lang?.name || lang;
                return formatDisplayText(langText);
              }).join(', ')}
            </InfoSection>
          )}

          {user.investment_style && (
            <InfoSection title={intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.investment_style' })}>
              {user.investment_style}
            </InfoSection>
          )}

          {user.service_model && (
            <InfoSection title={intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.service_model' })}>
              {user.service_model}
            </InfoSection>
          )}

          {user.primary_markets && user.primary_markets.length > 0 && (
            <InfoSection title={intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.primary_markets' })}>
              {renderList(user.primary_markets)}
            </InfoSection>
          )}

          {user.ask_me_about && (
            <InfoSection title={intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.ask_me_about' })}>
              {user.ask_me_about}
            </InfoSection>
          )}

          {user.specialized_businesses && user.specialized_businesses.length > 0 && (
            <InfoSection title={intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.specialized_businesses' })}>
              {renderList(user.specialized_businesses)}
            </InfoSection>
          )}

          {typeof user.discretionary_investment === 'boolean' && (
            <InfoSection title={intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.discretionary_investment' })}>
              {user.discretionary_investment ? intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.true' }) : intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.false' })}
            </InfoSection>
          )}

          {user.why_values && user.why_values.length > 0 && (
            <InfoSection title={intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.why_values' })}>
              {renderList(user.why_values)}
            </InfoSection>
          )}

          {user.interests && user.interests.length > 0 && (
            <InfoSection title={intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.interests_hobbies' })}>
              {renderList([...(user.interests || []), ...(user.sports || [])])}
            </InfoSection>
          )}
        </div>
      </div>

      {/* Practice Areas */}
      {user.practice_areas && user.practice_areas.length > 0 && (
        <div className="bg-succession-bg-secondary rounded-lg border border-succession-border p-4">
          <h4 className="text-sm font-semibold text-succession-text-primary mb-3">{intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.practice_areas' })}</h4>
          <div className="text-sm text-succession-text-muted">
            {renderList(user.practice_areas)}
          </div>
        </div>
      )}

      {/* Notes Section - Only show for other profiles */}
      {!isViewingSelf && showNotebookTab && (
        <div className="bg-succession-bg-secondary rounded-lg border border-succession-border p-4">
          <h4 className="text-sm font-semibold text-succession-text-primary mb-3">{intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.notes' })}</h4>
          
          {/* Add Note Input */}
          <div className="mb-4">
            <textarea
              value={noteText}
              onChange={(e) => onNoteTextChange && onNoteTextChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (noteText.trim() && onSubmitNote) {
                    onSubmitNote();
                  }
                }
              }}
              placeholder={intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.add_note_placeholder' })}
              className="w-full px-3 py-2 text-sm border border-succession-border rounded-lg bg-succession-bg-primary text-succession-text-primary placeholder-succession-text-muted focus:outline-none focus:ring-2 focus:ring-succession-primary resize-none"
              rows={2}
            />
            <button
              onClick={() => noteText.trim() && onSubmitNote && onSubmitNote()}
              disabled={!noteText.trim()}
              className="mt-2 w-full px-3 py-2 bg-succession-primary text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              {intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.add_note_button' })}
            </button>
          </div>

          {/* Notes List */}
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {userNotes.filter(note => note && note.comment && note.comment.trim()).length === 0 ? (
              <p className="text-xs text-succession-text-muted italic text-center py-4">{intl.formatMessage({ id: 'succession.profile_drawer.profile_sidebar.no_notes_yet' })}</p>
            ) : (
              userNotes
                .filter(note => note && note.comment && note.comment.trim())
                .map((note) => (
                <div key={note.id} className="bg-succession-bg-primary rounded-lg border border-succession-border p-3">
                  <div className="flex justify-between items-start gap-2">
                    <p className="text-xs text-succession-text-primary flex-1 whitespace-pre-wrap">{note.comment === 'Added to Rolodex' ? intl.formatMessage({ id: 'succession.profile_drawer.drawer.added_to_rolodex_note' }) : note.comment}</p>
                    <button
                      onClick={() => onDeleteNote && onDeleteNote(note.id)}
                      className="text-succession-text-muted hover:text-red-500 transition-colors flex-shrink-0"
                      title="Delete note"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                  {note.created_at && (
                    <p className="text-xs text-succession-text-muted mt-2">
                      {new Date(note.created_at).toLocaleDateString(intl.locale, {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* RJ Seller Visibility Workflow — buyer's one-time initiation modal.
          Only mounts when isRjVisibilityFlow. See docs/plans/2026-07-17-001-... */}
      <RjInitiateContactModal
        open={rjModalOpen}
        onClose={() => setRjModalOpen(false)}
        sellerUserId={user?.id}
        sellerName={user?.name || user?.allowed_name}
        onSuccess={() => {
          setRjAlreadyInitiatedLocal(true);
          enqueueSnackbar?.('Message sent. The advisor will decide whether to respond.', {
            variant: 'success',
            anchorOrigin: { vertical: 'top', horizontal: 'center' },
          });
        }}
        onAlreadyInitiated={() => {
          setRjAlreadyInitiatedLocal(true);
          enqueueSnackbar?.("You've already reached out to this advisor.", {
            variant: 'info',
            anchorOrigin: { vertical: 'top', horizontal: 'center' },
          });
        }}
      />
    </div>
  );
};

ProfileSidebar.propTypes = {
  user: PropTypes.shape({
    status: PropTypes.string,
    continuity_plan: PropTypes.string,
    languages: PropTypes.arrayOf(PropTypes.string),
    investment_style: PropTypes.string,
    service_model: PropTypes.string,
    primary_markets: PropTypes.arrayOf(PropTypes.string),
    ask_me_about: PropTypes.string,
    specialized_businesses: PropTypes.arrayOf(PropTypes.string),
    discretionary_investment: PropTypes.bool,
    why_values: PropTypes.arrayOf(PropTypes.string),
    practice_areas: PropTypes.arrayOf(PropTypes.string),
    interests: PropTypes.arrayOf(PropTypes.string),
    sports: PropTypes.arrayOf(PropTypes.string),
    buyer_assessment_result: PropTypes.shape({
      id: PropTypes.string,
      assessment_type: PropTypes.string,
      total_score: PropTypes.number,
      readiness_band: PropTypes.string,
      band_label: PropTypes.string,
      completed_at: PropTypes.string,
      section_scores: PropTypes.object,
    }),
  }).isRequired,
  isViewingSelf: PropTypes.bool.isRequired,
  showMessagesTab: PropTypes.bool.isRequired,
  showNotebookTab: PropTypes.bool.isRequired,
  canMessage: PropTypes.bool.isRequired,
  showInviteToDeal: PropTypes.bool,
  isInRolodex: PropTypes.bool.isRequired,
  onMessage: PropTypes.func,
  onAddToRolodex: PropTypes.func,
  userNotes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    comment: PropTypes.string,
    created_at: PropTypes.string,
  })),
  noteText: PropTypes.string,
  onNoteTextChange: PropTypes.func,
  onSubmitNote: PropTypes.func,
  onDeleteNote: PropTypes.func,
  currentUser: PropTypes.object,
  company: PropTypes.object,
  dealInterest: PropTypes.object,
  obfuscateView: PropTypes.bool,
  apolloClient: PropTypes.object,
  enqueueSnackbar: PropTypes.func,
  onScrollToDeal: PropTypes.func,
};

export default ProfileSidebar;

